// Gráfico de linha "Frequência por Área" (Painel do Gestor, Mensal e Período)
// — uma linha colorida por área (Educacional, Esportivo, Cultural,
// Tecnológico, Capelania), todas na mesma escala de 0-100% pra ficarem
// comparáveis mesmo com número de turmas bem diferente entre elas (ex.:
// Cultural tem dezenas de turmas numa instituição, Tecnológico só umas
// poucas — comparar CONTAGEM bruta não faria sentido, % sim). Construído à
// mão em SVG, mesma técnica de MonthlyTrendChart.jsx (não há biblioteca de
// gráficos no projeto).
//
// `dados` vem de GET /api/relatorios/estatisticas-{mensais,periodo}, campo
// `tendencia_por_area`: um objeto por dia letivo, com uma chave por área que
// tinha ao menos 1 turma esperada naquele dia (`{ data, educacional: 82,
// esportivo: 90, ... }`) — área sem NENHUMA turma na instituição inteira
// nunca aparece em nenhum dia; só essas são omitidas do gráfico (ver
// `areasComDado` abaixo), dias sem dado pontual dela caem em 0 (raro).
import React, { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { AREAS, AREA_LABEL } from '../utils/areas';

const ALTURA = 240;
const MARGEM = { topo: 16, direita: 12, baixo: 28, esquerda: 34 };

// Cor de traço por área (hex, pro SVG, porque `stroke` não aceita classe de
// cor de fundo do Tailwind) — paleta qualitativa (ColorBrewer "Dark2"),
// escolhida de propósito por ter as 5 cores o mais distantes possível umas
// das outras no círculo de matizes (ao contrário da paleta anterior, que
// tinha azul/ciano/roxo muito próximos e difíceis de diferenciar num
// gráfico de linha fina). Já não é a mesma associação de COR_KPI_POR_AREA em
// DailyReport.jsx — aqueles cards usam só as 7 cores fixas de KpiCard, que
// não tem opção pra reproduzir essa distância aqui.
const COR_AREA = {
  educacional: '#1b9e77', // verde-azulado
  esportivo: '#d95f02', // laranja
  cultural: '#7570b3', // roxo/índigo
  tecnologico: '#e7298a', // magenta
  capelania: '#66a61e', // verde-oliva
};

function formatarDataCurta(iso) {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

function caminhoSuave(pontos) {
  if (pontos.length === 0) return '';
  if (pontos.length === 1) return `M ${pontos[0].x} ${pontos[0].y}`;
  let d = `M ${pontos[0].x} ${pontos[0].y}`;
  for (let i = 0; i < pontos.length - 1; i++) {
    const p0 = pontos[i === 0 ? i : i - 1];
    const p1 = pontos[i];
    const p2 = pontos[i + 1];
    const p3 = pontos[i + 2 < pontos.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

function AreaFrequencyChart({ dados }) {
  const containerRef = useRef(null);
  const [largura, setLargura] = useState(600);
  const [hoverIdx, setHoverIdx] = useState(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setLargura(w);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const larguraUtil = Math.max(0, largura - MARGEM.esquerda - MARGEM.direita);
  const alturaUtil = ALTURA - MARGEM.topo - MARGEM.baixo;

  // Só entram no gráfico as áreas que aparecem em pelo menos um dia — uma
  // área sem nenhuma turma cadastrada na instituição (ex.: Capelania em
  // algumas unidades) nunca teria o que mostrar, uma linha travada em 0% o
  // tempo todo só seria ruído.
  const areasComDado = useMemo(() => {
    const presentes = new Set();
    dados.forEach((d) =>
      AREAS.forEach((a) => {
        if (d[a.value] !== undefined) presentes.add(a.value);
      }),
    );
    return AREAS.filter((a) => presentes.has(a.value));
  }, [dados]);

  const x = useCallback(
    (i) => {
      if (dados.length <= 1) return MARGEM.esquerda;
      return MARGEM.esquerda + (i / (dados.length - 1)) * larguraUtil;
    },
    [dados.length, larguraUtil],
  );

  const y = useCallback(
    (valor) => MARGEM.topo + alturaUtil - (valor / 100) * alturaUtil,
    [alturaUtil],
  );

  const linhasPorArea = useMemo(
    () =>
      areasComDado.map((area) => {
        const pontos = dados.map((d, i) => ({ x: x(i), y: y(d[area.value] ?? 0) }));
        return { area, pontos, caminho: caminhoSuave(pontos) };
      }),
    [areasComDado, dados, x, y],
  );

  const ticksY = [0, 25, 50, 75, 100];
  const passoRotuloX = Math.max(1, Math.ceil(dados.length / 7));

  const moverPonteiro = (evento) => {
    if (dados.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const posX = evento.clientX - rect.left;
    let melhorIdx = 0;
    let melhorDist = Infinity;
    dados.forEach((_, i) => {
      const dist = Math.abs(x(i) - posX);
      if (dist < melhorDist) {
        melhorDist = dist;
        melhorIdx = i;
      }
    });
    setHoverIdx(melhorIdx);
  };

  if (dados.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">
        Nenhum dia letivo nesse período ainda.
      </p>
    );
  }
  if (areasComDado.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">
        Nenhuma turma com área cadastrada nesse período.
      </p>
    );
  }

  const hover = hoverIdx !== null ? dados[hoverIdx] : null;

  return (
    <div>
      <div className="flex items-center gap-4 mb-2 flex-wrap">
        {areasComDado.map((a) => (
          <div
            key={a.value}
            className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
          >
            <span
              className="w-3 h-0.5 rounded-full"
              style={{ backgroundColor: COR_AREA[a.value] }}
            />
            {AREA_LABEL[a.value] || a.label}
          </div>
        ))}
      </div>

      <div ref={containerRef} className="relative">
        <svg
          width="100%"
          height={ALTURA}
          onPointerMove={moverPonteiro}
          onPointerLeave={() => setHoverIdx(null)}
          className="overflow-visible"
        >
          {ticksY.map((val) => (
            <g key={val}>
              <line
                x1={MARGEM.esquerda}
                x2={MARGEM.esquerda + larguraUtil}
                y1={y(val)}
                y2={y(val)}
                className="stroke-gray-100 dark:stroke-gray-800"
                strokeWidth={1}
                strokeDasharray="3 4"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={MARGEM.esquerda - 8}
                y={y(val)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-gray-400 dark:fill-gray-500 text-[10px]"
              >
                {val}%
              </text>
            </g>
          ))}

          {dados.map(
            (d, i) =>
              i % passoRotuloX === 0 && (
                <text
                  key={d.data}
                  x={x(i)}
                  y={ALTURA - 8}
                  textAnchor="middle"
                  className="fill-gray-400 dark:fill-gray-500 text-[10px]"
                >
                  {formatarDataCurta(d.data)}
                </text>
              ),
          )}

          {linhasPorArea.map(({ area, caminho }) => (
            <path
              key={area.value}
              d={caminho}
              fill="none"
              stroke={COR_AREA[area.value]}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {hover && (
            <>
              <line
                x1={x(hoverIdx)}
                x2={x(hoverIdx)}
                y1={MARGEM.topo}
                y2={MARGEM.topo + alturaUtil}
                className="stroke-gray-300 dark:stroke-gray-700"
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
              {linhasPorArea.map(({ area }) => (
                <circle
                  key={area.value}
                  cx={x(hoverIdx)}
                  cy={y(hover[area.value] ?? 0)}
                  r={4}
                  fill={COR_AREA[area.value]}
                  stroke="white"
                  strokeWidth={1.5}
                />
              ))}
            </>
          )}
        </svg>

        {hover && (
          <div
            className="absolute top-2 pointer-events-none bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-100 dark:border-gray-800 rounded-2xl shadow-xl px-3 py-2 text-xs min-w-[150px] overflow-hidden"
            style={{
              left: Math.min(Math.max(x(hoverIdx), 80), largura - 80),
              transform: 'translateX(-50%)',
            }}
          >
            <p className="font-medium text-gray-900 dark:text-gray-100 mb-1.5">
              {formatarDataCurta(hover.data)}
            </p>
            {areasComDado.map((a) => (
              <div key={a.value} className="flex items-center justify-between gap-3 mt-0.5">
                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-0.5 inline-block rounded-full"
                    style={{ backgroundColor: COR_AREA[a.value] }}
                  />
                  {AREA_LABEL[a.value] || a.label}
                </span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {hover[a.value] ?? 0}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AreaFrequencyChart;
